import React, { useState, useEffect } from 'react';
import './Navbar.css';
import logo from '../../images/logo.jpg';
import { FaPhoneAlt, FaBars, FaTimes, FaUser, FaChevronDown, FaCalendarCheck, FaSignOutAlt } from 'react-icons/fa';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { auth } from '../../firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { db } from '../../firebase';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [userFullName, setUserFullName] = useState(() => localStorage.getItem('userName') || '');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const navigate = useNavigate();
  const [navbarVisible, setNavbarVisible] = useState(true);


  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);

      const sections = ['cars', 'about', 'contact'];
      let currentSection = 'home';
      const scrollPosition = window.scrollY + window.innerHeight / 3;

      for (const sectionId of sections) {
        const section = document.getElementById(sectionId);
        if (section && scrollPosition >= section.offsetTop) {
          currentSection = sectionId;
        }
      }
      setActiveSection(currentSection);
    };

    if (location.pathname === '/') {
      window.addEventListener('scroll', handleScroll);
      handleScroll();
    } else {
      setActiveSection('');
      setIsScrolled(true);
    }
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  useEffect(() => {
    let ticking = false;
    let lastY = window.scrollY;
    function handleScrollAutoHide() {
      if (window.innerWidth > 900 || location.pathname === '/') {
        setNavbarVisible(true);
        return;
      }
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const currentY = window.scrollY;
          if (currentY < 0) return;
          if (currentY < lastY) {
            setNavbarVisible(true);
          } else if (currentY > lastY + 5) {
            setNavbarVisible(false);
          }
          lastY = currentY;
          ticking = false;
        });
        ticking = true;
      }
    }
    if (location.pathname !== '/') {
      window.addEventListener('scroll', handleScrollAutoHide);
    }
    return () => window.removeEventListener('scroll', handleScrollAutoHide);
  }, [location.pathname]);

  useEffect(() => {
    let isMounted = true;
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!isMounted) return;
      setUser(currentUser);
      if (currentUser) {
        // Immediate fallback to currentUser.displayName if available
        if (currentUser.displayName && currentUser.displayName.trim()) {
          setUserFullName(currentUser.displayName.trim());
          localStorage.setItem('userName', currentUser.displayName.trim());
        }

        const resolveNameFromData = (data) => {
          if (!data) return '';
          return (
            data.name ||
            data.fullName ||
            data.displayName ||
            data.userName ||
            data.firstName ||
            ''
          ).trim();
        };

        try {
          // 1. Check direct document by UID
          const userDocRef = doc(db, 'users', currentUser.uid);
          const directSnap = await getDoc(userDocRef);
          if (isMounted && directSnap.exists()) {
            const name = resolveNameFromData(directSnap.data());
            if (name) {
              setUserFullName(name);
              localStorage.setItem('userName', name);
              return;
            }
          }

          // 2. Query users collection where uid == currentUser.uid
          const uidQuery = query(collection(db, 'users'), where('uid', '==', currentUser.uid));
          const uidSnap = await getDocs(uidQuery);
          if (isMounted && !uidSnap.empty) {
            const name = resolveNameFromData(uidSnap.docs[0].data());
            if (name) {
              setUserFullName(name);
              localStorage.setItem('userName', name);
              return;
            }
          }

          // 3. Fallback query where email == currentUser.email
          if (currentUser.email) {
            const emailQuery = query(collection(db, 'users'), where('email', '==', currentUser.email));
            const emailSnap = await getDocs(emailQuery);
            if (isMounted && !emailSnap.empty) {
              const name = resolveNameFromData(emailSnap.docs[0].data());
              if (name) {
                setUserFullName(name);
                localStorage.setItem('userName', name);
                return;
              }
            }
          }

          // 4. Fallback to Firebase Auth displayName
          if (isMounted && currentUser.displayName && currentUser.displayName.trim()) {
            setUserFullName(currentUser.displayName.trim());
            localStorage.setItem('userName', currentUser.displayName.trim());
          }
        } catch (error) {
          console.warn('Error fetching user profile in Navbar:', error);
          if (isMounted && currentUser.displayName && currentUser.displayName.trim()) {
            setUserFullName(currentUser.displayName.trim());
          }
        }
      } else {
        setUserFullName('');
        localStorage.removeItem('userName');
      }
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);

  const getDisplayName = () => {
    if (userFullName && userFullName.trim()) {
      return userFullName.trim();
    }
    const cached = localStorage.getItem('userName');
    if (cached && cached.trim()) {
      return cached.trim();
    }
    if (user?.displayName && user.displayName.trim()) {
      return user.displayName.trim();
    }
    return 'User';
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const isLoginPage = location.pathname === '/login' || location.pathname === '/signup';

  const handleNavClick = (e, sectionId) => {
    if (location.pathname === '/') {
      e.preventDefault();
      document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    }
    if (isMobileMenuOpen) {
      toggleMobileMenu();
    }
  };

  const handleDropdownToggle = () => setDropdownOpen((open) => !open);
  const handleDropdownClose = () => setDropdownOpen(false);

  const handleLogout = async () => {
    localStorage.removeItem('userName');
    setUserFullName('');
    setUser(null);
    await signOut(auth);
    handleDropdownClose();
    if (isMobileMenuOpen) {
      setIsMobileMenuOpen(false);
    }
    navigate('/');
  };

  return (
    <>
      <nav className={`navbar${isScrolled ? ' scrolled' : ''}${navbarVisible ? '' : ' navbar-hidden'}`}>
        <div className="nav-container">
          <a href="/" className="nav-logo">
            <img src={logo} alt="Bappa Taxi Services" className="nav-logo-img" />
            <span>Bappa Taxi Services</span>
          </a>

          <div className="nav-links">
            <Link to="/" className={`nav-link ${activeSection === 'home' ? 'active' : ''}`}>Home</Link>
            <a href="/#cars" onClick={(e) => handleNavClick(e, 'cars')} className={`nav-link ${activeSection === 'cars' ? 'active' : ''}`}>Cars</a>
            <a href="/#about" onClick={(e) => handleNavClick(e, 'about')} className={`nav-link ${activeSection === 'about' ? 'active' : ''}`}>About</a>
            <a href="/#contact" onClick={(e) => handleNavClick(e, 'contact')} className={`nav-link ${activeSection === 'contact' ? 'active' : ''}`}>Contact</a>
          </div>

          <div className="nav-buttons" style={{ position: 'relative' }}>
            {!isLoginPage && (
              user ? (
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <button className="nav-button profile-button" style={{ marginRight: '10px', background: '#388e3c', color: '#fff', borderRadius: '6px' }} onClick={handleDropdownToggle}>
                    <FaUser style={{ marginRight: '6px' }} /> {getDisplayName()} <FaChevronDown style={{ marginLeft: '6px' }} />
                  </button>
                  {dropdownOpen && (
                    <div className="profile-dropdown" style={{ position: 'absolute', right: 0, top: '110%', background: '#fff', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', minWidth: '180px', zIndex: 1000 }} onMouseLeave={handleDropdownClose}>
                      <Link
                        to="/profile"
                        className="dropdown-item"
                        style={{ display: 'flex', alignItems: 'center', padding: '10px', color: '#388e3c', textDecoration: 'none', borderBottom: '1px solid #eee' }}
                        onClick={() => {
                          handleDropdownClose();
                          if (isMobileMenuOpen) toggleMobileMenu();
                        }}
                      >
                        <FaUser style={{ marginRight: '10px' }} /> My Profile
                      </Link>
                      <Link
                        to="/my-bookings"
                        className="dropdown-item"
                        style={{ display: 'flex', alignItems: 'center', padding: '10px', color: '#388e3c', textDecoration: 'none', borderBottom: '1px solid #eee' }}
                        onClick={() => {
                          handleDropdownClose();
                          if (isMobileMenuOpen) toggleMobileMenu();
                        }}
                      >
                        <FaCalendarCheck style={{ marginRight: '10px' }} /> My Bookings
                      </Link>
                      <button
                        type="button"
                        className="dropdown-item"
                        style={{ display: 'flex', alignItems: 'center', padding: '10px', color: 'red', background: 'none', border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer', font: 'inherit' }}
                        onClick={handleLogout}
                      >
                        <FaSignOutAlt style={{ marginRight: '10px' }} /> Logout
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Link to="/login" className="nav-button login-button">
                  <FaUser /> Login
                </Link>
              )
            )}
            <a
              href="tel:+919011333966"
              className="nav-button call-button"
              aria-label="Call Bappa Taxi Services now"
            >
              <FaPhoneAlt />Call Now
            </a>
          </div>

          {/* Hamburger and Close Button for Mobile */}
          {!isMobileMenuOpen && (
            <button className="menu-button" onClick={toggleMobileMenu}>
              <FaBars />
            </button>
          )}
          {isMobileMenuOpen && (
            <button className="menu-button" onClick={toggleMobileMenu}>
              <FaTimes />
            </button>
          )}
        </div>
      </nav>

      {/* Mobile Navigation */}
      <div className={`mobile-nav ${isMobileMenuOpen ? 'active' : ''}`}>
        <div className="mobile-nav-header">
          <span className="mobile-company-name">Bappa Taxi Services</span>
        </div>

        <div className="mobile-nav-links">
          <Link to="/" className={`mobile-nav-link ${activeSection === 'home' ? 'active' : ''}`} onClick={toggleMobileMenu}>Home</Link>
          <a href="/#cars" onClick={(e) => handleNavClick(e, 'cars')} className={`mobile-nav-link ${activeSection === 'cars' ? 'active' : ''}`}>Cars</a>
          <a href="/#about" onClick={(e) => handleNavClick(e, 'about')} className={`mobile-nav-link ${activeSection === 'about' ? 'active' : ''}`}>About</a>
          <a href="/#contact" onClick={(e) => handleNavClick(e, 'contact')} className={`mobile-nav-link ${activeSection === 'contact' ? 'active' : ''}`}>Contact</a>
        </div>

        <div className="mobile-nav-buttons" style={{ position: 'relative' }}>
          {!isLoginPage && (
            user ? (
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <button className="nav-button profile-button" style={{ marginRight: '10px', background: '#388e3c', color: '#fff', borderRadius: '6px' }} onClick={handleDropdownToggle}>
                  <FaUser style={{ marginRight: '6px' }} /> {getDisplayName()} <FaChevronDown style={{ marginLeft: '6px' }} />
                </button>
                {dropdownOpen && (
                  <div className="profile-dropdown" style={{ position: 'absolute', right: 0, top: '110%', background: '#fff', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', minWidth: '180px', zIndex: 1000 }} onMouseLeave={handleDropdownClose}>
                    <Link
                      to="/profile"
                      className="dropdown-item"
                      style={{ display: 'flex', alignItems: 'center', padding: '10px', color: '#388e3c', textDecoration: 'none', borderBottom: '1px solid #eee' }}
                      onClick={() => {
                        handleDropdownClose();
                        if (isMobileMenuOpen) toggleMobileMenu();
                      }}
                    >
                      <FaUser style={{ marginRight: '10px' }} /> My Profile
                    </Link>
                    <Link
                      to="/my-bookings"
                      className="dropdown-item"
                      style={{ display: 'flex', alignItems: 'center', padding: '10px', color: '#388e3c', textDecoration: 'none', borderBottom: '1px solid #eee' }}
                      onClick={() => {
                        handleDropdownClose();
                        if (isMobileMenuOpen) toggleMobileMenu();
                      }}
                    >
                      <FaCalendarCheck style={{ marginRight: '10px' }} /> My Bookings
                    </Link>
                    <button
                      type="button"
                      className="dropdown-item"
                      style={{ display: 'flex', alignItems: 'center', padding: '10px', color: 'red', background: 'none', border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer', font: 'inherit' }}
                      onClick={handleLogout}
                    >
                      <FaSignOutAlt style={{ marginRight: '10px' }} /> Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/login" className="nav-button login-button" onClick={toggleMobileMenu}>
                <FaUser /> Login
              </Link>
            )
          )}
          <a
            href="tel:+919011333966"
            className="nav-button call-button"
            aria-label="Call Bappa Taxi Services now"
          >
            Call Now <FaPhoneAlt />
          </a>
        </div>
      </div>
    </>
  );
};

export default Navbar; 